/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from "immer";
import Konva from "konva";
import React, { useEffect, useMemo, useRef } from "react";
import { Layer, Stage } from "react-konva";

import * as utils from "@examspark/common/dist/utils";
import { getScaledCoordinates, scaleImage } from "@examspark/common-util";
import { Stem } from "@examspark/types";

import {
  InlineChoice,
  MathEntry,
  TextEntry,
  ExtendedEntry,
} from "../../InlineText";
import GraphicGap from "../../shared/GraphicGap";
import { DndItem } from "../../shared/Token";

import BackgroundImage from "./BackgroundImage";
import CircleHotspot from "./CircleHotspot";
import Droppable from "./Droppable";
import PolygonHotspot from "./PolygonHotspot";
import RectangleHotspot from "./RectangleHotspot";

type GraphicDroppable = {
  type: "graphic";
  item: Stem.Token;
  drop?: Stem.Drop;
};
type TextDroppable = {
  type: "text";
  item: Stem.Option;
  drop?: Stem.Drop;
};
export type DroppableType = GraphicDroppable | TextDroppable;

interface Props {
  image: Stem.Image;
  width?: number;
  height?: number;
  map?: any;
  response?: any;
  scoring?: any;

  // todo: convert the rest of the item types to use this
  USE_NEW_DRAG?: true;

  /**
   * Rectangular hotspots that accept draggables. To be rendered as HTML `div`
   * elements outside of the Konva canvas. */
  droppables?: Array<Stem.Spot>;

  /**
   * gaps data that is used when shape === "text_gap"
   */
  gappables?: Stem.Gaps;

  /**
   * Components dropped inside of droppables.
   */
  drops?: Record<string, Array<DroppableType>>;

  marks?: Record<string, Stem.Mark>;

  gaps?: Stem.ResponseGaps;

  /**
   * Pass `useDrop` ref if you want to allow this component to receive draggables.
   */
  dropRef?: any;

  /** Fired when `ref` element's `current` property is set. */
  onInit?: (canvas: any) => void;
  onSpotClick?: (spot: Stem.Spot) => void;
  onClick?: (event: any) => void;
  onMouseMove?: (event: any) => void;
  onTokenDrop?: (gapId: string, item: DndItem) => void;
  onTokenRemove?: (gapId: string, item: DndItem) => void;
  onTextGapChange?: (gapId: string, value: string) => void;
}

const ImageMap: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const ref = useRef<Konva.Layer>(null);

  useEffect(() => {
    if (ref.current) {
      if (props.onInit) {
        const canvas = ref.current.canvas._canvas;
        props.onInit(canvas);
      }
    }
  }, [ref.current]);

  const handleSpotClick = (spot: Stem.Spot) => {
    if (props.onSpotClick) {
      props.onSpotClick(spot);
    }
  };

  const scale = useMemo(() => {
    let scaled = scaleImage(props.image);
    scaled = utils.geom.constrainDimensionsByWidth(
      scaled.width,
      scaled.height,
      650,
    );
    return scaled;
  }, [props.image]);

  // console.log("scale?", scale);

  return (
    <>
      <Stage
        width={scale.width}
        height={scale.height}
        title={props.image.metadata.alt}
        onMouseMove={(e: any) => {
          if (props.onMouseMove) {
            props.onMouseMove(e);
          }
        }}
        onClick={props.onClick}
        onTap={props.onClick}
      >
        <Layer ref={ref}>
          {props?.image?.file && (
            <BackgroundImage
              src={props.image.file}
              width={scale.width}
              height={scale.height}
            />
          )}
          {props?.map?.areas &&
            props.map.areas.map((s: Stem.Spot) => {
              /**
               * Scale hotspots
               *
               * Don't force the hotspots to compute/know about scale, let's
               * take care of that here and keep them as Pure as possible.
               */

              const spotData: Stem.Spot = produce(s, (draft) => {
                draft.coords = getScaledCoordinates(s.coords, scale.scale);
              });

              /**
               * Render hotspots
               */
              switch (s.shape) {
                case "circle":
                  return (
                    <CircleHotspot
                      key={s.id}
                      data={spotData}
                      onClick={() => {
                        handleSpotClick(s);
                      }}
                      onTap={() => {
                        handleSpotClick(s);
                      }}
                      {...s.props}
                    />
                  );
                case "rect":
                  return (
                    <RectangleHotspot
                      key={s.id}
                      data={spotData}
                      onClick={() => {
                        handleSpotClick(s);
                      }}
                      onTap={() => {
                        handleSpotClick(s);
                      }}
                      {...s.props}
                    />
                  );
                case "polygon":
                  return (
                    <PolygonHotspot
                      key={s.id}
                      data={spotData}
                      onClick={() => {
                        handleSpotClick(s);
                      }}
                      onTap={() => {
                        handleSpotClick(s);
                      }}
                      {...s.props}
                    />
                  );
                default:
                  return null;
              }
            })}
          {props.children}
        </Layer>
      </Stage>
      {/* Render div containers */}
      {props?.droppables &&
        props.droppables.map((s: Stem.Spot, idx: number) => {
          /**
           * Scale hotspots
           *
           * Don't force the hotspots to compute/know about scale, let's
           * take care of that here and keep them as Pure as possible.
           */
          const spotData: Stem.Spot = produce(s, (draft) => {
            draft.coords = getScaledCoordinates(s.coords, scale.scale);
          });
          const dropData = props?.drops?.[s.id]?.map((drop) => {
            if (drop.type === "graphic") {
              return {
                ...drop,
                item: produce(drop.item, (draft) => {
                  draft.metadata.height *= scale.scale;
                  draft.metadata.width *= scale.scale;
                }),
              };
            } else {
              return drop;
            }
          });

          /**
           * Render hotspots
           */
          if (s.shape === "rect") {
            if (props.USE_NEW_DRAG) {
              return (
                <GraphicGap
                  key={`droppable-${spotData.id}`}
                  gapId={spotData.id}
                  coords={spotData.coords}
                  dropped={dropData}
                  mark={props.marks?.[spotData.id]}
                  allowMultipleTokens={s.allowMultipleTokens}
                  onDrop={
                    props.onTokenDrop ??
                    (() => {
                      //
                      console.error(
                        "called onTokenDrop on a non-drop type image map",
                      );
                    })
                  }
                  onTokenRemove={
                    props.onTokenRemove ??
                    (() => {
                      //
                      console.error(
                        "called onTokenRemove on a non-drop type image map",
                      );
                    })
                  }
                />
              );
            } else {
              return (
                <Droppable key={`droppable-${s.id}`} data={spotData}>
                  {/* @ts-expect-error children */}
                  {props?.drops?.[s.id]}
                </Droppable>
              );
            }
          } else if (s.shape === "text_gap") {
            const gapData = props?.gappables?.[s.id];
            if (!gapData) {
              return null;
            }

            let GapElement = TextEntry;
            if (gapData.type === "inline-choice") {
              GapElement = InlineChoice;
            } else if (gapData.type === "inline-choice-richtext") {
              GapElement = InlineChoice;
            } else if (gapData.type === "math-entry") {
              GapElement = MathEntry;
            } else if (gapData.type === "extended-entry") {
              GapElement = ExtendedEntry;
            }

            return (
              <div
                key={`droppable-${s.id}`}
                style={{
                  position: "absolute",
                  left: `${spotData.coords.x}px`,
                  top: `${spotData.coords.y}px`,
                  width: `${spotData.coords.w}px`,
                  height: `${spotData.coords.h}px`,
                }}
              >
                <GapElement
                  interactionType="graphic-gap"
                  value={props.gaps?.[s.id] ?? ""}
                  mark={props.marks?.[s.id] ?? false}
                  options={gapData.options}
                  fontSize={gapData.fontSize}
                  textAlign={gapData.textAlign}
                  onChange={(value) => {
                    props.onTextGapChange?.(s.id, value);
                  }}
                ></GapElement>
              </div>
            );
          }

          return null;
        })}
    </>
  );
};
export default ImageMap;
